import React, {useContext, useEffect, useState} from 'react';
import SwiperCore, {A11y, Autoplay, Navigation, Pagination, Scrollbar} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import UserService from '../../services/user.service';
SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Autoplay]);
import 'swiper/swiper-bundle.min.css';
import {getLocaleContent} from '@helper/locale-content.js';
import {useRouter} from 'next/router';
import Link from 'next/link';
import {changeToSlug} from '@helper/string.helper';
import TNHContext from '@contexts/TNHContext';
import styles from './DoctorList.module.css';
import {safeHtml} from '@helper/sanitize-html';

export default function SlideHome() {
    const {companyId, setSelectedDoctor} = useContext(TNHContext);
    const [users, setUsers] = useState([]);
    const {locale} = useRouter();

    const handleButtonClick = user => {
        // Store selected doctor in context
        setSelectedDoctor({
            user_id: user.user_id,
            full_name: user.full_name,
            user_name: user.user_name,
            department_id: user.department_id,
            department_name: user.department_name,
            business_id: user.business_id,
        });

        // Navigate to booking form
        window.location.hash = '#dv-dat-lich';
        const el = document.getElementById('dv-dat-lich');
        if (el) el.scrollIntoView({behavior: 'smooth'});
    };

    useEffect(() => {
        const userService = new UserService();
        userService
            .getTopUserList({top: 20, companyId, locale})
            .then(response => {
                setUsers(response);
                console.log(res);
            })
            .catch(error => {
                console.error('Error fetching users:', error);
            });
    }, [companyId, locale]);

    const generateSlug = user => {
        const nameSlug = changeToSlug(user?.full_name);
        const id = user?.user_id;
        if (id) {
            return `${nameSlug}-u${id}`;
        }
        return '';
    };

    const swiperConfig = {
        autoplay: {
            delay: 2500,
            disableOnInteraction: false, // keep autoplay after any user interaction
            pauseOnMouseEnter: true, // optional: pause while hovering on desktop
        },
        loop: true,
        spaceBetween: 14,
        navigation: {
            nextEl: `.${styles['next-button']}`,
            prevEl: `.${styles['previous-button']}`,
        },
        breakpoints: {
            0: {
                slidesPerView: 1.1,
            },
            1024: {
                slidesPerView: 5,
            },
        },
        onSwiper: swiper => {
            // Ensure autoplay restarts after nav button clicks (some edge cases)
            const restart = () => {
                if (swiper.autoplay && swiper.autoplay.running === false) {
                    swiper.autoplay.start();
                }
            };
            const nextBtn = document.querySelector(`.${styles['next-button']}`);
            const prevBtn = document.querySelector(`.${styles['previous-button']}`);
            nextBtn && nextBtn.addEventListener('click', () => setTimeout(restart, 50));
            prevBtn && prevBtn.addEventListener('click', () => setTimeout(restart, 50));
        },
    };

    return (
        <>
            <section className="doctors">
                <div className="container">
                    <header className="doctors__head">
                        <h2 className="doctors__title">
                            {getLocaleContent(locale, 'page.doctors.title')}{' '}
                            <span>{getLocaleContent(locale, 'page.doctors.sub-title')}</span>
                        </h2>
                        <p className="doctors__lead">{getLocaleContent(locale, 'page.doctors.lead')}</p>
                    </header>
                    <div className={styles['swiper-container']}>
                        <button
                            className={`${styles['previous-button']} navbtn bw_hover_next`}
                            aria-label="Prev"></button>
                        <button className={`${styles['next-button']} navbtn bw_hover_prev`} aria-label="Next"></button>

                        <div className={styles['doctor-carousel']}>
                            <Swiper {...swiperConfig} className={styles['swiper']}>
                                {users.map((user, index) => {
                                    const cardContent = (
                                        <article className="doccard">
                                            <div className="doccard__media">
                                                <img src={user.image_url} alt={user.full_name} />
                                            </div>
                                            <div className="doccard__body">
                                                <span className="doccard__tag">{user.education_level_name || ' '}</span>
                                                <h3 className="doccard__name">{user.full_name}</h3>
                                                <p className="doccard__meta">
                                                    {user.position_name} {' - '}
                                                    {user.department_name}
                                                    {user?.business_name && (
                                                        <>
                                                            <br />
                                                            {user.business_name}
                                                        </>
                                                    )}
                                                    {user?.expertise && (
                                                        <>
                                                            <br />
                                                            {getLocaleContent(locale, 'page.doctors.expertise')}{' '}
                                                            {user.expertise}
                                                        </>
                                                    )}
                                                </p>
                                                <div className={styles['actions']}>
                                                    <Link
                                                        href={{
                                                            pathname: '/[slug]',
                                                            query: {slug: encodeURIComponent(generateSlug(user))},
                                                        }}>
                                                        <button className={styles['see-more']}>
                                                            {getLocaleContent(locale, 'common.see-more')}
                                                            <svg
                                                                style={{marginLeft: '5px'}}
                                                                width="18"
                                                                height="9"
                                                                viewBox="0 0 18 9"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg">
                                                                <path
                                                                    d="M13.8125 0.75L17.25 4.1875M17.25 4.1875L13.8125 7.625M17.25 4.1875H0.75"
                                                                    stroke="#5C5C5C"
                                                                    strokeWidth="1.5"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        </button>
                                                    </Link>
                                                    <button
                                                        className="doccard__cta"
                                                        onClick={e => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleButtonClick(user);
                                                        }}>
                                                        <img
                                                            className="icon"
                                                            src="/bw_image/ic_calendar_w.png"
                                                            alt=""
                                                        />
                                                        {getLocaleContent(locale, 'page.doctors.booking')}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="doccard__overlay">
                                                <div className="doccard__ov-wrap">
                                                    {user.education_level_name && (
                                                        <span className="doccard__tag">
                                                            {user.education_level_name}
                                                        </span>
                                                    )}
                                                    <h3 className="doccard__name">{user.full_name}</h3>
                                                    <p className="doccard__meta">
                                                        {user.position_name}
                                                        {' - '}
                                                        {user.department_name}
                                                    </p>
                                                    <h4 className="doccard__overlay-title">
                                                        {user?.business_name && <>{user.business_name}</>}
                                                        <br />
                                                        {user?.expertise && (
                                                            <>
                                                                <br />
                                                                {getLocaleContent(
                                                                    locale,
                                                                    'page.doctors.expertise',
                                                                )}{' '}
                                                                {user.expertise}
                                                                <br />
                                                            </>
                                                        )}
                                                        <br />
                                                        {getLocaleContent(locale, 'page.doctors.experience-department')}
                                                    </h4>
                                                    <div
                                                        className="doccard__list"
                                                        dangerouslySetInnerHTML={safeHtml(
                                                            user.about_me_short,
                                                        )}></div>
                                                    <div className={styles['actions']}>
                                                        <Link
                                                            href={{
                                                                pathname: '/[slug]',
                                                                query: {slug: encodeURIComponent(generateSlug(user))},
                                                            }}>
                                                            <button className={styles['see-more']}>
                                                                {getLocaleContent(locale, 'common.see-more')}
                                                                <svg
                                                                    style={{marginLeft: '5px'}}
                                                                    width="18"
                                                                    height="9"
                                                                    viewBox="0 0 18 9"
                                                                    fill="none"
                                                                    xmlns="http://www.w3.org/2000/svg">
                                                                    <path
                                                                        d="M13.8125 0.75L17.25 4.1875M17.25 4.1875L13.8125 7.625M17.25 4.1875H0.75"
                                                                        stroke="#5C5C5C"
                                                                        strokeWidth="1.5"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </svg>
                                                            </button>
                                                        </Link>
                                                        <button
                                                            className="doccard__cta"
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleButtonClick(user);
                                                            }}>
                                                            <img
                                                                className="icon"
                                                                src="/bw_image/ic_calendar_w.png"
                                                                alt=""
                                                            />
                                                            {getLocaleContent(locale, 'page.doctors.booking')}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </article>
                                    );

                                    const isDesktop = typeof window !== 'undefined' && window.innerWidth > 768;

                                    return (
                                        <SwiperSlide key={index} style={{width: '317px'}}>
                                            {isDesktop ? (
                                                <Link
                                                    href={{
                                                        pathname: '/[slug]',
                                                        query: {slug: encodeURIComponent(generateSlug(user))},
                                                    }}>
                                                    {cardContent}
                                                </Link>
                                            ) : (
                                                cardContent
                                            )}
                                        </SwiperSlide>
                                    );
                                })}
                            </Swiper>
                        </div>
                    </div>

                    <div className="doctors__more">
                        <Link href="/chuyen-gia-bac-si">
                            <a className="svc__all">
                                {getLocaleContent(locale, 'common.see-all')}
                                <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
                                    <path
                                        d="M8 5l8 7-8 7"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </a>
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
